import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Sparkles, Search, Settings, Save, FileText } from 'lucide-react';

const OnboardingTooltip = ({ onComplete }) => {
  const [step, setStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Check if user has seen onboarding
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('chekdeal_onboarding_complete');
    if (!hasSeenOnboarding) {
      // Delay showing to let page load
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const steps = [
    {
      icon: Search,
      title: 'Analyze a Property',
      description: 'Paste a listing URL from Zillow, Redfin, or Realtor.com - or enter an address manually with the asking price.',
      color: 'bg-blue-500'
    },
    {
      icon: Settings,
      title: 'Adjust Assumptions',
      description: 'Customize rehab condition, holding period, interest rates, and other deal assumptions to match your investment strategy.',
      color: 'bg-purple-500'
    },
    {
      icon: Save,
      title: 'Save & Compare',
      description: 'Save promising deals to your account and compare up to 3 properties side by side.',
      color: 'bg-green-500'
    },
    {
      icon: FileText,
      title: 'Share Reports',
      description: 'Download PDF reports or email them directly to partners and lenders - all from within the app.',
      color: 'bg-amber-500'
    }
  ];

  const handleComplete = () => {
    localStorage.setItem('chekdeal_onboarding_complete', 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  if (!isVisible) return null;

  const currentStep = steps[step];
  const StepIcon = currentStep.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" data-testid="onboarding-modal">
        {/* Header */}
        <div className={`${currentStep.color} p-6 text-white relative`}>
          <button
            onClick={handleComplete}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
            data-testid="onboarding-close"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm font-medium opacity-90">Welcome to CHEKSTE</span>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <StepIcon className="w-8 h-8" />
            </div>
            <div>
              <h3 className="text-xl font-bold">{currentStep.title}</h3>
              <p className="text-sm opacity-75">Step {step + 1} of {steps.length}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-muted-foreground leading-relaxed mb-6">
            {currentStep.description}
          </p>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  i === step ? 'bg-secondary w-6' : 'bg-slate-200 hover:bg-slate-300'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={handlePrev}
              disabled={step === 0}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            <button
              onClick={handleComplete}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Skip tour
            </button>

            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
              data-testid="onboarding-next"
            >
              {step === steps.length - 1 ? 'Get Started' : 'Next'}
              {step < steps.length - 1 && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTooltip;
